import { ChevronRight } from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { SidebarGlyphSlot } from '@/components/sidebar/sidebar-glyph-slot'
import { getSidebarIcon } from '@/components/sidebar/sidebar-icon-registry'
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
} from '@/components/ui/sidebar'
import { cn } from '@/lib/utils'
import type { DocumentPin } from '@/hooks/use-documents'
import type { SidebarMenuSelectedStyle } from '@/types/theme'

import { SidebarDocumentItem } from './sidebar-document-item'

type SidebarDocumentsNavProps = {
  label: string
  description?: string
  documentPins: DocumentPin[]
  openDocumentId: string | null
  isDocumentsActive: boolean
  menuLayout: 'horizontal' | 'vertical'
  isCollapsed: boolean
  selectedStyle?: SidebarMenuSelectedStyle | string | null
  onNavigateDocuments: () => void
  onOpenDocument: (documentId: string) => void
}

export function SidebarDocumentsNav({
  label,
  description,
  documentPins,
  openDocumentId,
  isDocumentsActive,
  menuLayout,
  isCollapsed,
  selectedStyle,
  onNavigateDocuments,
  onOpenDocument,
}: SidebarDocumentsNavProps) {
  const hasPins = documentPins.length > 0
  const isPinnedDocActive = documentPins.some((pin) => pin.document_id === openDocumentId)
  const [pinsOpen, setPinsOpen] = useState(hasPins)

  useEffect(() => {
    if (hasPins) {
      setPinsOpen(true)
    }
  }, [hasPins])

  useEffect(() => {
    if (isPinnedDocActive) {
      setPinsOpen(true)
    }
  }, [isPinnedDocActive, openDocumentId])

  const DocumentsIcon = getSidebarIcon('FileText')

  const documentsButton = (
    <SidebarMenuButton
      menuLayout={menuLayout}
      tooltip={isCollapsed ? (description ?? label) : undefined}
      onClick={onNavigateDocuments}
      isActive={isDocumentsActive && !isPinnedDocActive}
      selectedStyle={selectedStyle}
      className={cn(
        hasPins && !isCollapsed && 'min-w-0 flex-1',
        '[&>span:last-child]:overflow-visible [&>span:last-child]:whitespace-nowrap [&>span:last-child]:text-clip',
      )}
    >
      <SidebarGlyphSlot>
        <DocumentsIcon
          className={cn('size-4 shrink-0', menuLayout === 'horizontal' && 'text-sidebar-foreground')}
        />
      </SidebarGlyphSlot>
      {!isCollapsed && <span className="min-w-0 whitespace-nowrap">{label}</span>}
    </SidebarMenuButton>
  )

  if (!hasPins || isCollapsed) {
    return <SidebarMenuItem>{documentsButton}</SidebarMenuItem>
  }

  return (
    <Collapsible open={pinsOpen} onOpenChange={setPinsOpen} className="group/collapsible">
      <SidebarMenuItem>
        <div className="flex w-full min-w-0 items-center gap-0.5">
          {documentsButton}
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className={cn(
                'flex size-8 shrink-0 items-center justify-center rounded-md text-sidebar-foreground outline-none ring-sidebar-ring transition-colors',
                'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2',
              )}
              aria-label={pinsOpen ? 'Hide pinned documents' : 'Show pinned documents'}
              aria-expanded={pinsOpen}
            >
              <ChevronRight
                className={cn(
                  'size-4 shrink-0 transition-transform duration-200',
                  pinsOpen && 'rotate-90',
                )}
                aria-hidden
              />
            </button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <SidebarMenuSub className="mx-0 translate-x-0 border-0 px-0 py-0.5 pl-6">
            {documentPins.map((pin) => (
              <SidebarDocumentItem
                key={pin.id}
                documentId={pin.document_id}
                title={pin.title}
                onClick={onOpenDocument}
                selectedStyle={selectedStyle}
                isActive={openDocumentId === pin.document_id}
              />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}
